/**
 * Data Import Wizard — shared target-column eligibility gate (S6.T2a), and the
 * engine gate (which connections can be a destination at all) at the end.
 *
 * THE single shared gate for whether an existing-table target column can be
 * mapped at all, and whether it can additionally take a fixed value. Per the
 * Fable seam ruling ("one gate, no second list"), S6.T2c's mapping-rail
 * popover, S6.T4's fixed-value enable, AND the main-process Start-validation
 * all call this SAME function — there is no second, independently-maintained
 * eligibility list anywhere in the feature.
 *
 * Process-agnostic shared module (src/features/workspace/data-import/mapping/):
 * no React/zustand/DOM/electron-runtime imports. `ImportTargetColumn` is a
 * type-only import, erased at build — routed through `src/types/electron.d.ts`
 * (the codebase's sanctioned crossing point) rather than a direct
 * `electron/data-import/importTypes` import, which trips the
 * tsconfig.node.json composite project-reference boundary (TS6305) for a
 * fully-checked `src/` file.
 */
import type { ImportTargetColumn } from '../../../../types/electron';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { ActiveConnection } from '../../../connections/models/connectionTypes';

export type TargetEligibility = {
  mappable: boolean;
  fixedValueEligible: boolean;
  statusReason: string | null;
  /** Populated ONLY when `mappable` is true and `fixedValueEligible` is false — the human-readable
   *  reason the "Use fixed value…" affordance stays disabled for an otherwise-mappable column
   *  (S6.T4b). `statusReason` already covers the not-mappable case; this is computed by this SAME
   *  function (never a second, UI-layer re-derivation) so there is still only ONE eligibility gate. */
  fixedValueIneligibleReason?: string;
};

/** v1 fixed-value exclusion: the binary family + `unknown` (spatial/array/xml/
 *  enum all arrive as `kind: 'unknown'`) — no safe literal rendering for these. */
const FIXED_VALUE_EXCLUDED_KINDS = new Set(['binary', 'varbinary', 'blob', 'unknown']);

/**
 * @param col the raw target-column facts (never a precomputed verdict).
 * @param population the FROZEN post-filter `matchingRowCount` (import scope)
 *   the caller passes — never the raw source row count. This is a pure
 *   function: it never reads any store or IPC itself.
 */
export function targetEligibility(col: ImportTargetColumn, population: number): TargetEligibility {
  if (col.isComputedOrGenerated) {
    return { mappable: false, fixedValueEligible: false, statusReason: 'Computed by the database' };
  }
  if (col.isSyntheticRowid) {
    return { mappable: false, fixedValueEligible: false, statusReason: 'SQLite rowid — filled by the database' };
  }
  if (col.identityInsertable === false) {
    return { mappable: false, fixedValueEligible: false, statusReason: 'Generated identity — filled by the database' };
  }

  // A plain column, or an insertable identity (e.g. MSSQL) — mappable.
  const isFixedValueExcludedKind = FIXED_VALUE_EXCLUDED_KINDS.has(col.canonical.kind);
  const isIdentityMappingOnly = col.identityInsertable === true; // CP4 K3: identity is mapping-only, never a fixed constant
  const wouldViolateSingleColumnKey = (col.isPrimaryKey || col.isSingleColumnUnique) && population > 1;
  const fixedValueEligible = !isFixedValueExcludedKind && !isIdentityMappingOnly && !wouldViolateSingleColumnKey;

  let fixedValueIneligibleReason: string | undefined;
  if (isFixedValueExcludedKind) {
    fixedValueIneligibleReason = 'No safe fixed-value literal for this column type';
  } else if (isIdentityMappingOnly) {
    fixedValueIneligibleReason = 'An identity column can only be mapped from a file column, not a fixed value';
  } else if (wouldViolateSingleColumnKey) {
    fixedValueIneligibleReason = 'A repeated constant would violate the key'; // plan's Rev-7 wording
  }

  return { mappable: true, fixedValueEligible, statusReason: null, fixedValueIneligibleReason };
}

/**
 * The ENGINE gate, beside the column gate above: whether an engine can be a Data
 * Import destination. Kusto (Azure Data Explorer) and Azure Storage are not SQL
 * tables to write into, so their connections are never offered as targets and
 * never resolve to an import engine. The run-request resolvers take it as is: an
 * `undefined` engine there is a target that did not resolve (its connection was
 * removed mid-wizard) and is refused. The pickers read a connection's own
 * `engine` field through `supportsDataImportEngine` below instead.
 */
export type ImportTargetEngineId = Exclude<DatabaseEngineId, 'kusto' | 'azurestorage'>;

export function isImportTargetEngine(engine: DatabaseEngineId | string | undefined): engine is ImportTargetEngineId {
  return engine !== undefined && engine !== 'kusto' && engine !== 'azurestorage';
}

const UNSUPPORTED_REASONS: Record<Exclude<DatabaseEngineId, ImportTargetEngineId>, string> = {
  kusto: "Data import isn't available for Kusto (read-only) connections",
  azurestorage: "Data import isn't available for Azure Storage connections",
};

/**
 * The entry-point gate (More ▾ → Import Data…, Cmd/Ctrl+I and File → Import Data, the command palette, the Object
 * Explorer database and table menus) on top of `isImportTargetEngine`: why a connection of this engine cannot open the
 * wizard, or `undefined` when it can. An `undefined` engine is a legacy connection saved before the field existed —
 * mssql, as the toolbar's engine filters treat it (`engine ?? 'mssql'`, the repo's legacy-engine convention).
 */
export function dataImportUnsupportedReason(engine: string | undefined): string | undefined {
  const resolved = engine ?? 'mssql';
  return isImportTargetEngine(resolved) ? undefined : UNSUPPORTED_REASONS[resolved as keyof typeof UNSUPPORTED_REASONS];
}

/**
 * `dataImportUnsupportedReason` as a predicate — the palette's `engineFilter`, the menu builders' gate, and the wizard's
 * destination filters (`SourceStep`, `TargetStep`, `DestinationBar`): one reading of a connection's `engine` field, so a
 * legacy SQL Server record that the entry points open the wizard for is also offered as its destination (review round 4).
 */
export function supportsDataImportEngine(engine: string | undefined): boolean {
  return dataImportUnsupportedReason(engine) === undefined;
}

/**
 * The connection Import Data acts on (More ▾ → Import Data…, Cmd/Ctrl+I, File → Import Data): the one selected in the
 * Object Explorer, else the first connected connection that can be an import target, else the first connected one
 * (refused with its engine's reason). `openDataImportForActiveConnection` opens on it and `useToolbarGating` disables
 * the More ▾ item from it, so the item and the action never disagree.
 */
export function resolveDataImportConnection(
  selectedConnectionId: string | null | undefined,
  activeConnections: readonly ActiveConnection[],
): ActiveConnection | undefined {
  if (selectedConnectionId) return activeConnections.find((c) => c.id === selectedConnectionId);
  const connected = activeConnections.filter((c) => c.status === 'connected');
  return connected.find((c) => supportsDataImportEngine(c.connection?.engine)) ?? connected[0];
}
